import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

// Split text but preserve spacing
const tokenize = (text) => {
    const parts = text.match(/\S+|\s+/gu) || [];
    return parts.map((p) => ({ t: p, isWord: /\S/u.test(p) }));
};

export const VoiceAssistantTranscribe = () => {
    const containerRef = useRef(null);
    const lastWordCountsRef = useRef(new Map()); // id -> previous word count
    const caption = useSelector((s) => s.voiceAssistant.caption);
    const activeId = useSelector((s) => s.voiceAssistant.activeId);
    const lastLineRef = useRef(null);

    // Build render data (no hooks in loops)
    const renderData = caption.map((c) => {
        const tokens = tokenize(c.text);
        const wordIdxs = [];
        tokens.forEach((tok, i) => tok.isWord && wordIdxs.push(i));
        const currentWordCount = wordIdxs.length;
        const prevWordCount = lastWordCountsRef.current.get(c.id) ?? 0;

        // Words with index >= prevWordCount are "new" this render
        const newWordStart = Math.min(prevWordCount, currentWordCount);

        return { c, tokens, wordIdxs, currentWordCount, newWordStart };
    });

    // After each render, remember the latest counts (so next render knows what's new)
    useEffect(() => {
        for (const { c, currentWordCount } of renderData) {
            lastWordCountsRef.current.set(c.id, currentWordCount);
        }
    }, [caption]); // depends on caption array content

    // Auto-scroll: keep latest line centered
    useEffect(() => {
        if (lastLineRef.current) {
            lastLineRef.current.scrollIntoView({
                block: "center",
                behavior: "smooth",
            });
        }
    }, [caption]);

    return (
        <div className="va-transcribe" ref={containerRef}>
            {renderData.map(({ c, tokens, wordIdxs, newWordStart }, i) => {
                const isLastLine = i === renderData.length - 1;

                return (
                    <p
                        key={c.id}
                        ref={isLastLine ? lastLineRef : null}
                        className="va-line"
                        style={{ color: "#000" }}
                    >
                        {tokens.map((tok, idx) => {
                            if (!tok.isWord)
                                return <span key={idx}>{tok.t}</span>;
                            const wordOrder = wordIdxs.indexOf(idx);
                            const isNew = wordOrder >= newWordStart;
                            const delayMs = isNew
                                ? (wordOrder - newWordStart) * 20
                                : 0; // tiny stagger

                            // New words mount with .fade-in; old ones stay static
                            return (
                                <span
                                    key={idx}
                                    className={
                                        isNew ? "va-word fade-in" : "va-word"
                                    }
                                    style={
                                        isNew
                                            ? { animationDelay: `${delayMs}ms` }
                                            : undefined
                                    }
                                >
                                    {tok.t}
                                </span>
                            );
                        })}
                        {!c.done && c.id === activeId ? (
                            <span className="caret">▌</span>
                        ) : null}
                    </p>
                );
            })}
        </div>
    );
};
